/**
 * Start Source Multi-Select Component
 * Multi-select dropdown for session start source filter
 * Updated per HIVE-6245 requirement #3: Selected values should not appear on screen
 */

import { useState, useCallback } from 'react';
import { Button } from '@/core/components/ui/button';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import {
  StartSource,
  StartSourceLabels,
  type StartSourceType,
} from '../../types/filter-enums';

interface StartSourceMultiSelectProps {
  value: StartSourceType[];
  onChange: (value: StartSourceType[]) => void;
  onAutoRefresh?: () => void;  // Callback for auto-refresh on change
  className?: string;
}

export function StartSourceMultiSelect({
  value,
  onChange,
  onAutoRefresh,
  className = '',
}: StartSourceMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(
    (source: StartSourceType) => {
      let newValue: StartSourceType[];
      if (value.includes(source)) {
        newValue = value.filter((v) => v !== source);
      } else {
        newValue = [...value, source];
      }
      onChange(newValue);
      // Trigger auto-refresh when selection changes
      if (onAutoRefresh) {
        onAutoRefresh();
      }
    },
    [value, onChange, onAutoRefresh]
  );

  const handleClearAll = useCallback(() => {
    onChange([]);
    if (onAutoRefresh) {
      onAutoRefresh();
    }
  }, [onChange, onAutoRefresh]);

  const handleSelectAll = useCallback(() => {
    onChange(Object.values(StartSource));
    if (onAutoRefresh) {
      onAutoRefresh();
    }
  }, [onChange, onAutoRefresh]);

  const getDisplayText = () => {
    if (value.length === 0) return 'All';
    if (value.length === 1) return StartSourceLabels[value[0]];
    if (value.length === Object.values(StartSource).length) return 'All';
    return `${value.length} selected`;
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Label className="text-xs text-muted-foreground">Start Source</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 justify-between text-sm font-normal"
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <div className="p-2 border-b">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleSelectAll}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleClearAll}
              >
                Clear
              </Button>
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-2">
            {Object.values(StartSource).map((source) => (
              <div
                key={source}
                className="flex items-center gap-2 py-1.5 px-1 hover:bg-muted rounded cursor-pointer"
                onClick={() => handleToggle(source)}
              >
                <Checkbox
                  checked={value.includes(source)}
                  onCheckedChange={() => handleToggle(source)}
                />
                <span className="text-sm">{StartSourceLabels[source]}</span>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {/* HIVE-6245 #3: Selected values should not appear on screen - removed badges */}
    </div>
  );
}
